import type { SearchCriteria } from './types'

import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import defaultFetch from 'isomorphic-unfetch'
import {
  AbTests,
  OPTIMIZELY_TESTS_KEY,
  OPTIMIZELY_TESTS_KEYS,
} from '@/scopes/common/infrastructure/ab-testing/optimizely/types'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import { searchPassThroughParams } from '@/scopes/search/types'
import { createFiltersSelectionMapper, type FiltersSelectionMapper } from './filtersSelectionMapper'
import { createSearchResponseMapper, type SearchResponseMapper } from './searchResponseMapper'

interface Dependencies<F> {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  filterSelectionMapper: FiltersSelectionMapper<F>
  queryStringSerializer: QueryStringSerializer<SearchCriteria<F> & { contextItemId: string, promotionItemId?: string, accoIds?: string[] }>
  searchResponseMapper: SearchResponseMapper
  contextItemId: string
  browserQuery: BrowserQueryString
  promotionId?: string
  abTests: AbTests
}

export function createSearchApiClient<F>(dependencies: Partial<Dependencies<F>> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    filterSelectionMapper = createFiltersSelectionMapper<F>(),
    queryStringSerializer = createQueryStringSerializer(),
    searchResponseMapper = createSearchResponseMapper(),
    contextItemId = '',
    promotionId,
    browserQuery = createBrowserQueryString(),
    abTests = {},
  } = dependencies as Dependencies<F>

  const EnabledBTests = (Object.keys(abTests) as OPTIMIZELY_TESTS_KEY[])
    .filter(key => abTests[key])
    .map(key => OPTIMIZELY_TESTS_KEYS[key])
    .join(',')

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(EnabledBTests && { EnabledBTests }),
    },
  }

  async function getSearchResults(criteria: SearchCriteria<F>) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)
    const data = await response.json()

    return searchResponseMapper.map(data)
  }

  function buildAPIUrl(criteria: SearchCriteria<F>) {
    const pagination = criteria.pagination ?? {}
    const sorting = getSortingFromCriteria(criteria)
    const filterSelection = getMappedFiltersFromCriteria(criteria)

    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getPassThroughParams(queryParams, searchPassThroughParams)
    const requiredFiltersParam = criteria.requiredFilters?.join('%2B')

    const queryString = queryStringSerializer.serialize({
      contextItemId,
      promotionItemId: promotionId,
      ...pagination,
      ...sorting,
      ...filterSelection,
      ...passThroughParams,
      ...getAccommodationIdsFromCriteria(criteria),
    })

    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/SearchApi/GetSearchResponse?${queryString}`
    if (requiredFiltersParam) {
      return `${url}&requiredFilters=${requiredFiltersParam}`
    }
    return url
  }

  function getSortingFromCriteria(criteria: SearchCriteria<F>) {
    return criteria.sorting ? { sort: criteria.sorting.by } : {}
  }

  function getAccommodationIdsFromCriteria(criteria: SearchCriteria<F>) {
    return criteria.accommodationIds?.length ? { accoIds: criteria.accommodationIds } : {}
  }

  function getMappedFiltersFromCriteria(criteria: SearchCriteria<F>) {
    return criteria.filterSelection ? filterSelectionMapper.map(criteria.filterSelection) : {}
  }

  async function getAIFilters(userQuery: string) {
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/SearchApi/GetAIFilters?query=${userQuery}`

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        method: 'POST',
        body: null,
      })

      const data = await response.json()
      return searchResponseMapper.mapAIFilters(data)
    }
    catch (error) {
      console.error('Error fetching AI filters: ', error)
      throw error
    }
  }

  return { getSearchResults, getAIFilters }
}
